/**
 * The Cortex Console Decisions blade (harness row CC2-10; spec.md 8A ViewDecisions,
 * requirements.md FR-1 to FR-5 and FR-16, design.md sections 6 and 11).
 *
 * What this component does: render the answer `GET /api/cortex/console/decisions`
 * gave - the pending decisions in the order the server listed them, the recently ruled
 * ones below, the scope it applied, and for each decision the options as stored with
 * the recommended one marked, who it is for and how long it has waited. Where the
 * answer says this caller may rule a decision, it offers the rule control.
 *
 * What it deliberately does NOT do:
 *
 * - decide who may rule. `may_rule` arrives on each item, and the control is shown
 *   when it is true and never otherwise (design.md section 2, decision row DP-CC-30);
 * - decide whether a decision has waited too long. `stale` is the server's verdict
 *   against its own configured window, and nothing here reads a clock;
 * - invent an option. A decision recorded without options is shown without any and
 *   offers no rule control (requirements.md FR-2);
 * - name who is ruling. A ruling request carries the decision and the choice only; the
 *   server takes the person from the signed-in token;
 * - show a disk path. The answer carries none, and nothing here reads a file.
 */
import React from "react";
import { ContentRegionLoader } from "../components/loading";
import type {
  ConsoleAgentsScope,
  ConsoleDecisionItem,
  ConsoleDecisionsResponse,
  ConsoleRefusal,
  ConsoleRuleResponse,
  ConsoleRulingChoice,
} from "../api/types";
import { idLabel, roleLabel } from "../labels/displayLabels";
import { plainDuration } from "./AgentsBlade";
import { reported } from "./OverviewBlade";
import styles from "./DecisionsBlade.module.css";

export function isDecisions(
  body: ConsoleDecisionsResponse | ConsoleRefusal,
): body is ConsoleDecisionsResponse {
  return (body as ConsoleDecisionsResponse).response_type === "console_decisions";
}

/**
 * How long a decision has waited, in the words a person uses. Presentation only: the
 * number of minutes is the server's, and whether that is too long is the server's
 * `stale` verdict, not a comparison made here.
 */
export function waitedFor(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) {
    return "not reported";
  }
  const wholeMinutes = Math.floor(minutes);
  if (wholeMinutes < 60) {
    return `${wholeMinutes} ${wholeMinutes === 1 ? "minute" : "minutes"}`;
  }
  const hours = Math.floor(wholeMinutes / 60);
  if (hours < 48) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return `${Math.floor(hours / 24)} days`;
}

/**
 * Who a decision waits on. The answer sends the persisted role id; a person reads it
 * through the display-label mapping (harness row CC2-11, DP-CC-03).
 */
export function routedToLine(item: ConsoleDecisionItem): string {
  return `For: ${roleLabel(item.routed_to_role)}`;
}

/**
 * A rule-route answer that actually settled a decision, never a refusal or an
 * in-flight moment (design.md section 6: "client-side optimistic update from the
 * `console_decision_ruled` response").
 */
export function isRuledSuccess(
  answer: ConsoleRuleResponse | ConsoleRefusal | null,
): answer is ConsoleRuleResponse {
  return (
    !!answer &&
    answer.response_type === "console_decision_ruled" &&
    answer.outcome_type === "success"
  );
}

/**
 * The ruling as the settle answer names it: a lettered choice carries only its letter
 * on the wire, so the option's own recorded text is read back off the item that was
 * ruled; a written ruling carries its own words directly.
 */
function settledRulingLine(item: ConsoleDecisionItem, answer: ConsoleRuleResponse): string {
  if (answer.chosen_option) {
    const matched = item.options.find((option) => option.letter === answer.chosen_option);
    return `Ruling: ${answer.chosen_option}. ${matched ? matched.text : reported(answer.ruling_text)}`;
  }
  return `Ruling: ${reported(answer.ruling_text)}`;
}

/** The ruling as recorded: the letter chosen, then the words of the ruling. */
export function rulingLine(item: ConsoleDecisionItem): string {
  const letter = item.chosen_option ? `${item.chosen_option}. ` : "";
  return `Ruling: ${letter}${reported(item.ruling_text)}`;
}

type RuleHandler = (
  decisionKey: string,
  choice: ConsoleRulingChoice,
) => Promise<ConsoleRuleResponse | ConsoleRefusal>;

export type DecisionsBladeProps = {
  decisions: ConsoleDecisionsResponse | ConsoleRefusal | null;
  loading: boolean;
  /** The scope the person asked for. What was applied is on the answer itself. */
  requestedScope: ConsoleAgentsScope;
  onScopeChange: (scope: ConsoleAgentsScope) => void;
  onRefresh: () => void;
  onRule: RuleHandler;
  /** A decision a person followed here from the Review blade, when they did. */
  followedDecisionKey?: string | null;
};

// The radio value for "write the ruling instead". Not a letter any option can carry,
// because an option letter is never empty and never starts with a hyphen.
const WRITE_OWN_RULING = "-write-";

function Options({ item }: { item: ConsoleDecisionItem }) {
  if (item.options.length === 0) {
    return (
      <p className={styles.line} data-testid={`decision-no-options-${item.decision_key}`}>
        No options were recorded for this decision.
      </p>
    );
  }
  return (
    <ol className={styles.options} data-testid={`decision-options-${item.decision_key}`}>
      {item.options.map((option) => (
        <li
          key={option.letter}
          className={styles.option}
          data-testid={`decision-option-${item.decision_key}-${option.letter}`}
        >
          {option.letter}. {option.text}
          {option.recommended ? (
            <span className={styles.recommended} data-testid="decision-option-recommended">
              Recommended
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function RuleControl({ item, onRule }: { item: ConsoleDecisionItem; onRule: RuleHandler }) {
  const [choice, setChoice] = React.useState("");
  const [ownWords, setOwnWords] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const key = item.decision_key;
  const writing = choice === WRITE_OWN_RULING;
  const ready = choice !== "" && (!writing || ownWords.trim() !== "");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || sending) {
      return;
    }
    setSending(true);
    try {
      await onRule(
        key,
        writing ? { ruling_text: ownWords.trim() } : { chosen_option: choice },
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      className={styles.rule}
      data-testid={`decision-rule-${key}`}
      onSubmit={(event) => void submit(event)}
    >
      <fieldset className={styles.ruleFieldset}>
        <legend className={styles.ruleLegend}>Rule this decision</legend>
        {item.options.map((option) => (
          <label
            key={option.letter}
            className={styles.ruleChoice}
            data-testid={`decision-rule-letter-${key}-${option.letter}`}
          >
            <input
              type="radio"
              name={`rule-${key}`}
              value={option.letter}
              checked={choice === option.letter}
              onChange={() => setChoice(option.letter)}
              data-testid={`decision-rule-choice-${key}-${option.letter}`}
            />
            {option.letter}. {option.text}
            {option.recommended ? (
              <span className={styles.recommended} data-testid="decision-option-recommended">
                Recommended
              </span>
            ) : null}
          </label>
        ))}
        <label className={styles.ruleChoice} data-testid={`decision-rule-other-${key}`}>
          <input
            type="radio"
            name={`rule-${key}`}
            value={WRITE_OWN_RULING}
            checked={writing}
            onChange={() => setChoice(WRITE_OWN_RULING)}
            data-testid={`decision-rule-choice-${key}-own-words`}
          />
          Write the ruling in your own words
        </label>
        {writing ? (
          <textarea
            className={styles.ruleText}
            aria-label="Your ruling"
            value={ownWords}
            onChange={(event) => setOwnWords(event.target.value)}
            data-testid={`decision-rule-text-${key}`}
          />
        ) : null}
      </fieldset>
      <button
        type="submit"
        className={styles.confirm}
        disabled={!ready || sending}
        data-testid={`decision-rule-confirm-${key}`}
      >
        Confirm ruling
      </button>
    </form>
  );
}

/**
 * A decision followed here from another blade (the Review link) is scrolled to and
 * marked. Which decision that is comes from the link, not from anything worked out here.
 */
function useFollowed(followed: boolean) {
  const ref = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    if (followed) {
      ref.current?.scrollIntoView?.({ block: "center" });
    }
  }, [followed]);
  return {
    ref,
    className: followed ? `${styles.card} ${styles.followed}` : styles.card,
    "data-followed": followed ? "true" : undefined,
  };
}

/**
 * The interim, self-contained modal (design.md section 11) a card opens on click.
 * Retired in favour of the shared Console Design System modal primitive once it
 * publishes; its public surface (open/close, content slot) is kept minimal so that
 * swap is a drop-in replacement, not a rewrite.
 */
function DecisionModal({
  decisionKey,
  onClose,
  children,
}: {
  decisionKey: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={styles.modalOverlay}
      data-testid={`decision-modal-${decisionKey}`}
      role="dialog"
      aria-modal="true"
      // Clicks and keystrokes anywhere inside the modal (the action bar, the
      // close button, the own-words textarea) must never reach the card's own
      // onClick/onKeyDown behind it - that would immediately reopen a modal the
      // click just closed, or eat a space keystroke typed into the textarea.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className={styles.modalContent}>
        <button
          type="button"
          className={styles.modalClose}
          data-testid={`decision-modal-close-${decisionKey}`}
          onClick={onClose}
        >
          Close
        </button>
        {children}
      </div>
    </div>
  );
}

/** Full context (spec.md 8A "Card modal" structure item 1), read verbatim from the item. */
function ModalFullContext({ item }: { item: ConsoleDecisionItem }) {
  return (
    <div className={styles.detail}>
      <p className={styles.line}>Decision: {item.decision_key}</p>
      <p className={styles.line}>Stream: {reported(item.stream)}</p>
      <p className={styles.line}>Question: {reported(item.question)}</p>
      <p className={styles.line}>Marking: {reported(item.classification)}</p>
      <p className={styles.line}>Asked: {reported(item.requested_at)}</p>
      <p className={styles.line}>
        Recommended option: {item.recommendation || "none marked"}
      </p>
      {item.ruled_via ? (
        <p className={styles.line}>Ruled through: {idLabel(item.ruled_via)}</p>
      ) : null}
    </div>
  );
}

/**
 * What the modal shows in place of the action bar the moment a ruling succeeds
 * (requirements.md FR-RD-3): no live button remains, the ruling itself is shown,
 * read from the settle answer rather than waiting on the list to refetch.
 */
function SettledRuling({
  item,
  answer,
}: {
  item: ConsoleDecisionItem;
  answer: ConsoleRuleResponse;
}) {
  return (
    <div className={styles.detail}>
      <p className={styles.line} data-testid="decision-ruled-by">
        Ruled by: {reported(answer.ruled_by && roleLabel(answer.ruled_by))}
      </p>
      <p className={styles.line} data-testid="decision-ruled-at">
        Ruled at: {reported(answer.ruled_at)}
      </p>
      <p className={styles.line} data-testid="decision-ruling">
        {settledRulingLine(item, answer)}
      </p>
    </div>
  );
}

/**
 * Who ruled a ruled decision, what was chosen, and when (requirements.md FR-VD-2),
 * read straight off the item as the server recorded it - never an action bar, since
 * this decision is already settled (design.md section 6).
 */
function RuledFullDetail({ item }: { item: ConsoleDecisionItem }) {
  return (
    <div className={styles.detail}>
      <p className={styles.line} data-testid="decision-ruled-by">
        Ruled by: {reported(item.ruled_by && roleLabel(item.ruled_by))}
      </p>
      <p className={styles.line} data-testid="decision-ruled-at">
        Ruled at: {reported(item.ruled_at)}
      </p>
      <p className={styles.line} data-testid="decision-ruling">
        {rulingLine(item)}
      </p>
    </div>
  );
}

function PendingDecision({
  item,
  staleAfterSeconds,
  onRule,
  followed,
}: {
  item: ConsoleDecisionItem;
  staleAfterSeconds: number | null;
  onRule: RuleHandler;
  followed: boolean;
}) {
  // A decision followed here from the Review blade arrives with its modal already
  // open (design.md Section 6 resolved default) - the initial value only, since a
  // fresh navigation is what mounts this card in the first place.
  const [open, setOpen] = React.useState(followed);
  const [settled, setSettled] = React.useState<ConsoleRuleResponse | null>(null);
  const key = item.decision_key;

  const handleRule: RuleHandler = async (decisionKey, choice) => {
    const answer = await onRule(decisionKey, choice);
    if (isRuledSuccess(answer)) {
      setSettled(answer);
    }
    return answer;
  };

  return (
    <article
      {...useFollowed(followed)}
      data-testid={`decision-pending-${key}`}
      role="button"
      tabIndex={0}
      onClick={() => setOpen(true)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
      }}
    >
      <div className={styles.cardHead}>
        <h4 className={styles.cardTitle}>{reported(item.question)}</h4>
        {item.stale ? (
          <span className={styles.stale} data-testid="decision-stale">
            {staleAfterSeconds === null
              ? "Waiting longer than Cortex expects"
              : `Waiting longer than ${plainDuration(staleAfterSeconds)}`}
          </span>
        ) : null}
      </div>
      <p className={styles.line}>Stream: {reported(item.stream)}</p>
      <p className={styles.line} data-testid="decision-routed-to">
        {routedToLine(item)}
      </p>
      <p className={styles.line} data-testid="decision-waited">
        Waiting: {waitedFor(item.age_minutes)}
      </p>
      <Options item={item} />
      {open ? (
        <DecisionModal decisionKey={key} onClose={() => setOpen(false)}>
          <ModalFullContext item={item} />
          {settled ? (
            <SettledRuling item={item} answer={settled} />
          ) : item.may_rule ? (
            <RuleControl item={item} onRule={handleRule} />
          ) : null}
        </DecisionModal>
      ) : null}
    </article>
  );
}

function RuledDecision({ item, followed }: { item: ConsoleDecisionItem; followed: boolean }) {
  // Same followed-arrives-open default as PendingDecision (design.md Section 6).
  const [open, setOpen] = React.useState(followed);
  const key = item.decision_key;

  return (
    <article
      {...useFollowed(followed)}
      data-testid={`decision-ruled-${key}`}
      role="button"
      tabIndex={0}
      onClick={() => setOpen(true)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
      }}
    >
      <h4 className={styles.cardTitle}>{reported(item.question)}</h4>
      <p className={styles.line}>Stream: {reported(item.stream)}</p>
      <p className={styles.line}>Ruled at: {reported(item.ruled_at)}</p>
      <Options item={item} />
      {open ? (
        <DecisionModal decisionKey={key} onClose={() => setOpen(false)}>
          <ModalFullContext item={item} />
          <RuledFullDetail item={item} />
        </DecisionModal>
      ) : null}
    </article>
  );
}

export function DecisionsBlade({
  decisions,
  loading,
  requestedScope,
  onScopeChange,
  onRefresh,
  onRule,
  followedDecisionKey = null,
}: DecisionsBladeProps) {
  const answered = decisions && isDecisions(decisions) ? decisions : null;
  const message = decisions?.message;
  const pending = answered?.pending;
  const ruled = answered?.ruled;
  const windows = answered?.windows;
  const followedIsListed = [...(pending || []), ...(ruled || [])].some(
    (item) => item.decision_key === followedDecisionKey,
  );
  const [ruleAnswer, setRuleAnswer] = React.useState<
    ConsoleRuleResponse | ConsoleRefusal | null
  >(null);

  const rule: RuleHandler = async (decisionKey, choice) => {
    setRuleAnswer(null);
    const answer = await onRule(decisionKey, choice);
    setRuleAnswer(answer);
    return answer;
  };

  return (
    <div className={styles.blade} data-testid="console-decisions-blade">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Decisions</h2>
          {answered?.scope_statement ? (
            <p className={styles.scopeStatement} data-testid="decisions-scope-statement">
              {answered.scope_statement}
            </p>
          ) : null}
        </div>
        <div className={styles.controls}>
          <label className={styles.switch}>
            <input
              type="checkbox"
              data-testid="decisions-all-streams-switch"
              checked={requestedScope === "all_streams"}
              onChange={(event) =>
                onScopeChange(event.target.checked ? "all_streams" : "granted_streams")
              }
            />
            All streams
          </label>
          <button
            type="button"
            className={styles.refresh}
            data-testid="decisions-refresh"
            onClick={onRefresh}
          >
            Refresh
          </button>
        </div>
      </div>

      {ruleAnswer?.message ? (
        <p className={styles.message} data-testid="decisions-rule-answer">
          {ruleAnswer.message}
        </p>
      ) : null}

      {message ? (
        <p className={styles.message} data-testid="decisions-message">
          {message}
        </p>
      ) : null}

      {!loading && pending && ruled && followedDecisionKey && !followedIsListed ? (
        <p className={styles.line} data-testid="decisions-followed-not-listed">
          The decision you followed ({followedDecisionKey}) is not in this list.
        </p>
      ) : null}

      {loading ? (
        <ContentRegionLoader label="Loading decisions" data-testid="decisions-loading" />
      ) : null}

      {!loading && pending ? (
        <section className={styles.section} data-testid="decisions-pending">
          <h3 className={styles.sectionTitle}>Waiting for a ruling</h3>
          {pending.length === 0 ? (
            <p className={styles.line}>Nothing is waiting for a ruling here.</p>
          ) : (
            pending.map((item) => (
              <PendingDecision
                key={item.decision_key}
                item={item}
                staleAfterSeconds={windows ? windows.stale_after_seconds : null}
                onRule={rule}
                followed={item.decision_key === followedDecisionKey}
              />
            ))
          )}
        </section>
      ) : null}

      {!loading && ruled ? (
        <section className={styles.section} data-testid="decisions-ruled">
          <h3 className={styles.sectionTitle}>Ruled recently</h3>
          {windows ? (
            <p className={styles.provenance} data-testid="decisions-ruled-window">
              A ruling stays here for {plainDuration(windows.ruled_visible_for_seconds)}.
            </p>
          ) : null}
          {ruled.length === 0 ? (
            <p className={styles.line}>Nothing was ruled recently.</p>
          ) : (
            ruled.map((item) => (
              <RuledDecision
                key={item.decision_key}
                item={item}
                followed={item.decision_key === followedDecisionKey}
              />
            ))
          )}
        </section>
      ) : null}
    </div>
  );
}
